/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, flow, computed } from 'mobx';
import fetch from '../fetch';
import get from 'lodash/get';
import set from 'lodash/set';
import size from 'lodash/size';
import map from 'lodash/map';
import { isError } from './actionCreator';
import ErrorsStore from './errorsStore';
import _ from '../translations';
import { captureExceptionSentry } from '../utils/sentry';
class AppStore {
    @observable app = {};
    @observable apps = [];
    @observable appGroups = [];
    @observable selectedAppIndex = 0;
    @observable
    versions = {
        ios: false,
        android: false,
        web: false,
    };
    @observable loading = false;
    @observable version = false;
    @observable releaseNotes = false;

    constructor() {
        this.getApp = this.getApp.bind(this);
        this.getApps = this.getApps.bind(this);
        this.getVersions = this.getVersions.bind(this);
        const localIndex = localStorage.getItem('_nunchee_console_app_index');
        if (localIndex) this.selectedAppIndex = JSON.parse(localIndex);
        this.baseUrl = '/api/core/apps';
    }

    @action
    reset = () => {
        this.app = {};
        localStorage.removeItem('_nunchee_console_app_index');
    };

    @action.bound
    getApp = flow(function* () {
        this.loading = true;
        try {
            const result = yield fetch.get({
                url: this.baseUrl,
                params: { sortByAsc: '_id', itemsPerPage: 100 },
            });
            if (isError(result)) {
                ErrorsStore.addError(
                    {
                        message: _('There was an error getting app info'),
                    },
                    _('Nunchee services are down right now, try again later')
                );
                return;
            } else {
                const items = get(result, 'items', [])
                if (size(items) <= this.selectedAppIndex) {
                    this.selectedAppIndex = 0;
                }
                let app = get(items, `[${this.selectedAppIndex}]`, {});
                this.app = app;
            }
        } catch (e) {
            console.warn(e);
        } finally {
            this.loading = false;
        }
    });

    @action.bound
    getApps = flow(function* () {
        this.loading = true;
        try {
            const result = yield fetch.get({
                url: this.baseUrl,
                params: { sortByAsc: '_id', itemsPerPage: 100 },
            });
            if (isError(result)) {
                ErrorsStore.addError(
                    {
                        message: _('There was an error getting app info'),
                    },
                    _('Nunchee services are down right now, try again later')
                );
                return;
            } else {
                let apps = get(result, `items`, []);
                this.apps = apps;
            }
        } catch (e) {
            console.warn(e);
        } finally {
            this.loading = false;
        }
    });

    @action.bound
    setAppIndex = (index = 0) => {
        this.loading = true;
        try {
            this.selectedAppIndex = index;
            localStorage.setItem('_nunchee_console_app_index', index);
            this.getApp();
        } catch (e) {
            console.warn(e);
        } finally {
            this.loading = false;
        }
    }

    @action.bound
    setGatewaysConfig = (gatewayConfig) => {
        set(this.app, 'settings.gateways', gatewayConfig);
    }

    @action.bound
    getConsoleVersion = flow(function* () {
        try {
            const result = yield fetch.get({
                url: '/api/version',
                params: {},
            });
            if (isError(result)) {
                return;
            } else {
                const version = get(result, 'version', false);
                const notes = get(result, 'notes', false);
                if (version !== this.version) {
                    this.version = version;
                    this.releaseNotes = notes;
                }
            }
            return result;
        } catch (e) {
            console.warn(e);
        }
    });

    @action.bound
    updateApp = flow(function* (app = {}) {
        if (app.secret) delete app.secret;
        this.loading = true;
        try {
            yield fetch.patch({
                url: `${this.baseUrl}/multiple`,
                body: { data: { items: [app] } },
            });
            this.getApp();
        } catch (e) {
            captureExceptionSentry(e);
            console.warn(e);
        } finally {
            this.loading = false;
        }
    });

    @action.bound
    replaceApp = flow(function* (app = {}) {
        this.loading = true;
        try {
            const res = yield fetch.put({
                url: `${this.baseUrl}/multiple`,
                body: { data: { items: [app] } },
            });
            yield this.getApp();
            return res;
        } catch (e) {
            captureExceptionSentry(e);
            console.warn(e);
            return false;
        } finally {
            this.loading = false;
        }
    });

    getVersions = flow(function* (params = {}) {
        try {
            const result = yield fetch.get({
                url: `${this.baseUrl}/versions`,
                params,
            });
            return get(result, 'items', []);
        } catch (e) {
            captureExceptionSentry(e);
            console.warn(e);
        }
    });

    @action.bound
    getLastVersions = flow(function* () {
        try {
            const ios = this.getVersions({
                'filterBy-platform': 'ios',
                sortBy: '_createDate',
                itemsPerPage: 1,
            });
            const android = this.getVersions({
                'filterBy-platform': 'android',
                sortBy: '_createDate',
                itemsPerPage: 1,
            });
            const web = this.getVersions({
                'filterBy-platform': 'web',
                sortBy: '_createDate',
                itemsPerPage: 1,
            });

            const result = [yield ios, yield android, yield web];
            this.versions = {
                ios: result[0][this.selectedAppIndex],
                android: result[1][this.selectedAppIndex],
                web: result[2][this.selectedAppIndex],
            };
        } catch (e) {
            console.warn(e);
        }
    });

    @action.bound
    retrieveMpBaseUrl = flow(function* () {
        try {
            const check = yield fetch.get({
                url: window.mpSites,
                params: {},
            });
            if (isError(check)) {
                console.log('check', check);
                //ErrorsStore.addError(check, '');
            }
            return check;
        } catch (e) {
            console.warn(e);
            // ErrorsStore.addError(e, 'There was a problem getting the data');
            return;
        }
    });

    @action.bound
    getAppGroups = flow(function* (id = '') {
        try {
            const result = yield fetch.get({
                url: `${this.baseUrl}/groups`,
            });
            this.appGroups = result
        } catch (e) {
            console.warn(e);
        }
    });

    @computed
    get forSelect() {
        return map(this.apps ? this.apps : [], item => ({
            text: item.name,
            value: item._id,
        }));
    }

    @computed
    get forAppCodeSelect() {
        return map(this.apps ? this.apps : [], item => ({
            text: item.name,
            value: item.code,
        }));
    }

    @computed
    get appGroupsForSelect() {
        return map(this.appGroups?.items ? this.appGroups?.items : [], item => ({
            text: item.name,
            value: item._id,
        }));
    }
}

export default new AppStore();
